// OrderStatus import removed as it's not used in this file

export interface OrderDateRangeRequest {
  dateFrom: string;
  dateTo: string;
  branchId?: string;
  cloudBranchId?: string;
}

export interface DailyOrderReport {
  // Restaurant name from configuration
  restaurantName: string;
  
  // Local calendar day label in Africa/Cairo timezone (YYYY-MM-DD)
  // Serialized from C# property `Date` (computed from businessDate)
  date: string | null;

  // Counts and totals
  countOfAllOrders: number;

  totalCashOrders: number;
  countOfCashOrders: number;

  totalVisaOrders: number;
  countOfVisaOrders: number;

  totalCancelledOrders: number;
  countOfCancelledOrders: number;

  

  // Cancelled after closed
  totalCancelledAfterClosedOrders: number;
  countOfCancelledAfterClosedOrders: number;
  // Cancelled before close
  totalCancelledBeforeCloseOrders: number;
  countOfCancelledBeforeCloseOrders: number;

  totalVisaCancelledOrders: number;
  countOfVisaCancelledOrders: number;

  totalVisaCancelledAfterClosedOrders: number;
  countOfVisaCancelledAfterClosedOrders: number;

  totalVisaCancelledBeforeClosedOrders: number;
  countOfVisaCancelledBeforeClosedOrders: number;

  totalCashCancelledOrders: number;
  countOfCashCancelledOrders: number;

  totalCashCancelledAfterClosedOrders: number;
  countOfCashCancelledAfterClosedOrders: number;  
  totalCashCancelledBeforeClosedOrders: number;
  countOfCashCancelledBeforeClosedOrders: number;

  totalDiscountOfVisaOrders: number;
  countOfVisaOrdersWithDiscount: number;

  totalDiscountOfCashOrders: number;
  countOfCashOrdersWithDiscount: number;

  // Cloud short numbers (backend may return "N/A")
  startCloudShortNumber?: string | null;
  endCloudShortNumber?: string | null;

  // Provider and items totals
  totalClouldProviderSales: number; // Note: "Clould" matches backend spelling
  totalSales: number;
}

export interface MonthlyOrderReport {
  year: number;
  month: number;
  monthName: string;
  totalOrders: number;
  cancelledOrders: number;
  startCloudShortNumber: string;
  endCloudShortNumber: string;
  totalCloud: number;
  totalRevenue: number;
}

export interface SalesSummaryReport {
  // Restaurant name from configuration
  restaurantName: string;
  
  // Period Information
  periodStart: string;
  periodEnd: string;
  
  // Core Order Metrics
  countOfAllOrders: number;
  totalCashOrders: number;
  countOfCashOrders: number;
  totalVisaOrders: number;
  countOfVisaOrders: number;
  
  // Cancellation Metrics
  countOfCancelledOrders: number;
  totalCancelledOrders: number;
  
  // Cancelled after closed
  totalCancelledAfterClosedOrders: number;
  countOfCancelledAfterClosedOrders: number;
  // Cancelled before close
  totalCancelledBeforeCloseOrders: number;
  countOfCancelledBeforeCloseOrders: number;

  totalVisaCancelledOrders: number;
  countOfVisaCancelledOrders: number;

  totalVisaCancelledAfterClosedOrders: number;
  countOfVisaCancelledAfterClosedOrders: number;

  totalVisaCancelledBeforeClosedOrders: number;
  countOfVisaCancelledBeforeClosedOrders: number;

  totalCashCancelledOrders: number;
  countOfCashCancelledOrders: number;

  totalCashCancelledAfterClosedOrders: number;
  countOfCashCancelledAfterClosedOrders: number;  
  totalCashCancelledBeforeClosedOrders: number;
  countOfCashCancelledBeforeClosedOrders: number;

  // Discount Metrics
  totalDiscountOfVisaOrders: number;
  countOfVisaOrdersWithDiscount: number;
  totalDiscountOfCashOrders: number;
  countOfCashOrdersWithDiscount: number;

  // Sales Totals
  totalClouldProviderSales: number; // Note: "Clould" matches backend spelling
  totalSales: number;
}

// Financial Settlement Report Types
export interface CashOrdersBreakdown {
  totalCashOrdersSummary: {
    orderCount: number;
    totalOrderValue: number;
    totalDiscounts: number;
    totalReceivedByRestaurant: number;
    moneyHolder: string;
    description: string;
  };
  cashDiscounts: {
    discountedOrderCount: number;
    totalDiscountAmount: number;
    moneyHolder: string;
    settlement: string;
    description: string;
  };
  talabatCommission: {
    commissionRate: number;
    commissionAmount: number;
    calculationBase: string;
    settlement: string;
    description: string;
    cashDelivered: number;
    deliveryNote: string;
  };
  cancellations: {
    cancelledCount: number;
    totalCancelledValue: number;
    penaltyRate: number;
    penaltyAmount: number;
    status: string;
    description: string;
  };
}

export interface OnlineOrdersBreakdown {
  totalOnlineOrdersSummary: {
    orderCount: number;
    totalOrderValue: number;
    totalDiscounts: number;
    totalReceivedByTalabat: number;
    moneyHolder: string;
    description: string;
  };
  onlineDiscounts: {
    discountedOrderCount: number;
    totalDiscountAmount: number;
    impact: string;
    settlement: string;
    description: string;
  };
  talabatCommission: {
    commissionRate: number;
    commissionAmount: number;
    calculationBase: string;
    moneyHolder: string;
    description: string;
  };
  restaurantShare: {
    shareRate: number;
    netAmount: number;
    settlement: string;
    description: string;
  };
  cancellations: {
    cancelledCount: number;
    totalCancelledValue: number;
    penaltyRate: number;
    penaltyAmount: number;
    status: string;
    description: string;
  };
}

export interface SettlementSummary {
  restaurantOwesToTalabat: {
    cashTalabatCommission: number;
    cancellationPenalties: number;
    total: number;
    description: string;
  };
  talabatOwesToRestaurant: {
    onlineRestaurantShare: number;
    cashDiscountReimbursement: number;
    onlineDiscountReimbursement: number;
    total: number;
    description: string;
  };
  moneyAlreadyProperlyAllocated: {
    onlineCommissions: number;
    cashReceivedByRestaurant: number;
    description: string;
  };
  netSettlementPosition: {
    netSettlementAmount: number;
    settlementDirection: string;
    calculation: string;
    description: string;
  };
}

export interface FinancialSettlementReport {
  periodStart: string;
  periodEnd: string;
  restaurantInfo: {
    restaurantName: string;
  };
  cashOrdersBreakdown: CashOrdersBreakdown;
  onlineOrdersBreakdown: OnlineOrdersBreakdown;
  settlementSummary: SettlementSummary;
}

export interface AllDayCheckDto {
  orderId: string;
  shortCode: string;
  localOrderNumber: string;
  orderDate: string;
  restaurantName: string;
  branchName: string;
  status: string;
  cloudStatus: string;
  totalAmount: number;
  totalItems: number;
  paymentMethod: string;
}

export enum ReportType {
  DAILY_ORDERS = 'daily-orders',
  SALES_SUMMARY = 'sales-summary',
  FINANCIAL_SETTLEMENT = 'financial-settlement',
  ALL_DAY_CHECKS = 'all-day-checks' // New report type
}
